import {
  ComparisonString,
  ComparisonInteger,
  NodeConditionCheckOrderLineKeyValuePair
} from "lib/types/workflowConditions"
import { CHECK_KIND_LEAF, CheckLeaf, LeafCheck } from "lib/types/workflow"
import type { FieldDescriptor } from "./conditionSchema"

// ──────────────────────────────────────────────────────────────────────────
// Default-value factories for fresh editor-state instances
// ──────────────────────────────────────────────────────────────────────────

export const makeDefaultComparisonString = (): ComparisonString => ({
  comparison: "EQUALS",
  values: []
})

export const makeDefaultComparisonInteger = (): ComparisonInteger => ({
  comparison: "EQUALS",
  values: []
})

export const makeDefaultLeaf = (): CheckLeaf => ({
  kind: CHECK_KIND_LEAF,
  // PAYMENT_PROVIDER with no fields — invalid by itself; editor prompts the
  // user to add at least one field via the descriptor's selectable list.
  check: { type: "PAYMENT_PROVIDER", fields: {} }
})

// ──────────────────────────────────────────────────────────────────────────
// Path helpers — read / write / unset values at dot-notation paths inside
// the `fields` object of a LeafCheck.
// ──────────────────────────────────────────────────────────────────────────

export const getAtPath = (root: unknown, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>(
      (acc, key) =>
        acc && typeof acc === "object"
          ? (acc as Record<string, unknown>)[key]
          : undefined,
      root
    )

type AnyRecord = Record<string, unknown>

const toRecord = (v: unknown): AnyRecord =>
  v && typeof v === "object" ? ({ ...(v as AnyRecord) }) : {}

export const setAtPath = (
  root: unknown,
  path: string,
  value: unknown
): AnyRecord => {
  const segments = path.split(".")
  const next: AnyRecord = toRecord(root)
  let cursor: AnyRecord = next
  for (let i = 0; i < segments.length - 1; i++) {
    const key = segments[i]
    const child = toRecord(cursor[key])
    cursor[key] = child
    cursor = child
  }
  cursor[segments[segments.length - 1]] = value
  return next
}

/** Remove the leaf at `path` and prune empty parent objects. */
export const unsetAtPath = (root: unknown, path: string): AnyRecord => {
  if (!root || typeof root !== "object") return {}
  const segments = path.split(".")
  const next: AnyRecord = { ...(root as AnyRecord) }
  const stack: AnyRecord[] = []
  let cursor: AnyRecord = next
  for (let i = 0; i < segments.length - 1; i++) {
    const key = segments[i]
    const existing = cursor[key]
    if (!existing || typeof existing !== "object") return next
    const copy: AnyRecord = { ...(existing as AnyRecord) }
    cursor[key] = copy
    stack.push(cursor)
    cursor = copy
  }
  delete cursor[segments[segments.length - 1]]
  // Prune empty intermediates.
  for (let i = stack.length - 1; i >= 0; i--) {
    const parent = stack[i]
    const childKey = segments[i]
    const child = parent[childKey]
    if (
      child &&
      typeof child === "object" &&
      Object.keys(child as AnyRecord).length === 0
    ) {
      delete parent[childKey]
    }
  }
  return next
}

// ──────────────────────────────────────────────────────────────────────────
// Default-value factories per field kind
// ──────────────────────────────────────────────────────────────────────────

export const makeDefaultForFieldKind = (kind: FieldDescriptor["kind"]): unknown => {
  switch (kind) {
    case "comparisonString":
    case "comparisonStringEnum":
      return makeDefaultComparisonString()
    case "comparisonInteger":
      return makeDefaultComparisonInteger()
    case "arrayCount":
      return { length: makeDefaultComparisonInteger() }
    case "keyValues":
      return {
        keyValues: [makeEmptyKeyValuePair()]
      }
  }
}

export const makeEmptyKeyValuePair = (): NodeConditionCheckOrderLineKeyValuePair => ({
  key: makeDefaultComparisonString()
})

// ──────────────────────────────────────────────────────────────────────────
// Set / unset a descriptor field on a LeafCheck.
//
// Three shape categories:
//   - ORDER_DATE / CUSTOM_HTTP — no nested fields; not editable here.
//   - ORDER_LINES / SHIPPING_FEES / GIFTS — `count.length` only.
//   - everything else — a `fields` field-bag, shape varies per check type.
//
// `setAtPath` / `unsetAtPath` work on a plain record, so we hand them the
// inner blob and rewrap. The single end-of-function `as LeafCheck` is the
// only cast — TypeScript can't narrow a value back into the union after we
// rebuild it from a loose record, but the runtime shape is unchanged.
// ──────────────────────────────────────────────────────────────────────────

export const setLeafField = (
  check: LeafCheck,
  path: string,
  value: unknown
): LeafCheck => {
  if (check.type === "ORDER_DATE" || check.type === "CUSTOM_HTTP") {
    return check
  }
  if (
    check.type === "ORDER_LINES" ||
    check.type === "SHIPPING_FEES" ||
    check.type === "GIFTS"
  ) {
    return {
      ...check,
      count: setAtPath(check.count, path, value)
    } as unknown as LeafCheck
  }
  const fields = (check as unknown as { fields?: AnyRecord }).fields
  return {
    ...check,
    fields: setAtPath(fields, path, value)
  } as unknown as LeafCheck
}

export const unsetLeafField = (check: LeafCheck, path: string): LeafCheck => {
  if (check.type === "ORDER_DATE" || check.type === "CUSTOM_HTTP") {
    return check
  }
  if (
    check.type === "ORDER_LINES" ||
    check.type === "SHIPPING_FEES" ||
    check.type === "GIFTS"
  ) {
    return {
      ...check,
      count: unsetAtPath(check.count, path)
    } as unknown as LeafCheck
  }
  const fields = (check as unknown as { fields?: AnyRecord }).fields
  return {
    ...check,
    fields: unsetAtPath(fields, path)
  } as unknown as LeafCheck
}

export const isFieldSet = (check: LeafCheck, path: string): boolean => {
  if (
    check.type === "ORDER_LINES" ||
    check.type === "SHIPPING_FEES" ||
    check.type === "GIFTS"
  ) {
    return getAtPath(check.count, path) !== undefined
  }
  const fields = (check as { fields?: unknown }).fields
  return getAtPath(fields, path) !== undefined
}

export const getLeafField = (check: LeafCheck, path: string): unknown => {
  if (
    check.type === "ORDER_LINES" ||
    check.type === "SHIPPING_FEES" ||
    check.type === "GIFTS"
  ) {
    return getAtPath(check.count, path)
  }
  const fields = (check as { fields?: unknown }).fields
  return getAtPath(fields, path)
}

export const asComparisonString = (v: unknown): ComparisonString | undefined =>
  v && typeof v === "object" ? (v as ComparisonString) : undefined

export const asComparisonInteger = (v: unknown): ComparisonInteger | undefined =>
  v && typeof v === "object" ? (v as ComparisonInteger) : undefined
